import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

import {
  selectWebAppStateMySelf,
  selectWebAppStateMySelfHasAnalyticsAccess,
} from '../../store/getters/web-app.getters'
import { setShowSnackBar } from '../../store/reducers/web-app.reducer'
import { buildRelativePath, PathConfig } from '../../core/config/PathConfig'
import { analyticsDetailV2StateNewAnalyticAction } from '../../store/actions/analytics-details/analytic-details-v2.actions'
import AnalyticDetailsLayout from '../../components/analytics-details-v2/AnalyticDetailsLayout'
import {
  selectAnalyticDetailsStateAnalyticDetails,
  selectAnalyticDetailsStateIsLoadingGetAnalyticDetails,
  selectAnalyticDetailsStateNeedSaveAnalyticDetails,
} from '../../store/getters/analytics-details/analytic-details-v2.getters'
import { reinitializeAnalyticDetailsV2State } from '../../store/reducers/analytics-details/analytic-details-v2.reducer'
import { AnalyticsTypeEnum } from '../../core/enums/analytics-type.enum'
import GlDialog from '../../components/share/dialog/GlDialog'
import {
  selectNewAnalyticsReadershipProfileStatePostPreviewError,
  selectNewAnalyticsReadershipProfileStatePostPreviewTypeError,
  selectNewAnalyticsReadershipProfileStateStep,
} from '../../store/getters/analytics-details/new-analytics-readership-profile.getters'
import { AnalyticPreviewTypeErrorEnum } from '../../core/enums/analytic-preview-type-error'
import BackGroundPreviewTypeError from '../../assets/images/preview-report-type-error.svg'
import { patchReportService } from '../../core/api/services/bookmetrie/reports.service'
import { AnalyticsStatusEnum } from '../../core/enums/analytics-status.enum'
import { AnalyticsPermissionEnum } from '../../core/enums/analytics-permission.enum'
import GlDialogBody from '../../components/share/dialog/GlDialogBody'
import GlDialogTitle from '../../components/share/dialog/GlDialogTitle'
import GLDialogButtonStyle from '../../components/share/dialog/GLDialogButtonStyle'
import AnalyticsDialogExit from '../../components/share/dialog/AnalyticsDialogExit'
import { NewReadershipProfileStepEnum } from '../../store/interfaces/analytics-details/new-readership-profile-state.interface'
import AnalyticDetailsNewRequestStartStep from '../../components/analytics-details-v2/view/readership-profile/AnalyticDetailsNewRequestStartStep'
import AnalyticDetailsRequestPreviewStep from '../../components/analytics-details-v2/view/readership-profile/AnalyticDetailsRequestPreviewStep'
import AnalyticDetailsNewRequestDetailsStep from '../../components/analytics-details-v2/view/readership-profile/AnalyticDetailsNewRequestDetailsStep'
import { reinitializeNewReadershipProfileState } from '../../store/reducers/analytics-details/new-analytics-readership-profile-state.reducer'

const PrivateNewReadershipProfileView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [saveBeforeExitDialog, setSaveBeforeExitDialog] =
    useState<boolean>(false)
  const [displayPreviewErrorDialog, setDisplayPreviewErrorDialog] =
    useState<boolean>(true)

  const dispatch = useAppDispatch()

  const isLoadingGetAnalyticDetails = useAppSelector(
    selectAnalyticDetailsStateIsLoadingGetAnalyticDetails,
  )
  const analyticDetails = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetails,
  )
  const mySelf = useAppSelector(selectWebAppStateMySelf)
  const hasAnalyticsAccess = useAppSelector(
    selectWebAppStateMySelfHasAnalyticsAccess,
  )
  const needSaveAnalyticDetails = useAppSelector(
    selectAnalyticDetailsStateNeedSaveAnalyticDetails,
  )
  const postPreviewError = useAppSelector(
    selectNewAnalyticsReadershipProfileStatePostPreviewError,
  )
  const postPreviewTypeError = useAppSelector(
    selectNewAnalyticsReadershipProfileStatePostPreviewTypeError,
  )
  const step = useAppSelector(selectNewAnalyticsReadershipProfileStateStep)

  useEffect(() => {
    if (mySelf && !hasAnalyticsAccess) {
      dispatch(setShowSnackBar(t('webApp.notAccessToFeature')))
      navigate(buildRelativePath([PathConfig.BOOKMETRIE]))
    }
  }, [mySelf, hasAnalyticsAccess])

  useEffect(() => {
    if (hasAnalyticsAccess) {
      void dispatch(
        analyticsDetailV2StateNewAnalyticAction({
          analyticType: AnalyticsTypeEnum.PRIVATE_READERSHIP_PROFILE,
        }),
      )
    }

    return () => {
      dispatch(reinitializeAnalyticDetailsV2State())
      dispatch(reinitializeNewReadershipProfileState())
    }
  }, [])

  useEffect(() => {
    if (postPreviewError && !displayPreviewErrorDialog) {
      setDisplayPreviewErrorDialog(true)
    }
  }, [postPreviewError])

  const handleRenameAnalytic = (newName: string) => {
    if (newName === analyticDetails.name) return
    // TODO : cet endpoint a pas l'air de fonctionner
    patchReportService(analyticDetails.id, { name: newName })
      .then(() => {
        dispatch(
          setShowSnackBar(
            t('snackbarMessage.renameReportConfirm', {
              reportName: newName,
            }),
          ),
        )
      })
      .catch(() => {
        dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
      })
  }

  const onValidateExitDialog = useCallback(() => {
    if (saveBeforeExitDialog) {
      navigate(
        buildRelativePath([PathConfig.BOOKMETRIE, PathConfig.PRIVATE_REPORTS]),
      )
    }
    setSaveBeforeExitDialog(false)
  }, [saveBeforeExitDialog])

  const onCloseExitDialog = useCallback(() => {
    setSaveBeforeExitDialog(false)
  }, [])

  return (
    <>
      <AnalyticDetailsLayout
        handleRenameAnalytic={handleRenameAnalytic}
        analyticName={analyticDetails?.name || ''}
        sharedBy={[
          analyticDetails?.shared_by?.first_name,
          analyticDetails?.shared_by?.last_name,
        ]
          .join(' ')
          .trim()}
        disableEdit={
          analyticDetails?.status !== AnalyticsStatusEnum.GENERATED ||
          analyticDetails.permission === AnalyticsPermissionEnum.READ
        }
        hasButtonPrevious={false}
        isLoading={isLoadingGetAnalyticDetails}
        handleExitAnalyticDetails={() => {
          if (needSaveAnalyticDetails) {
            setSaveBeforeExitDialog(true)
          } else {
            return navigate(
              buildRelativePath([
                PathConfig.BOOKMETRIE,
                PathConfig.PRIVATE_REPORTS,
              ]),
            )
          }
        }}
      >
        {step === NewReadershipProfileStepEnum.START ? (
          <AnalyticDetailsNewRequestStartStep isPrivate />
        ) : step === NewReadershipProfileStepEnum.DETAILS ? (
          <AnalyticDetailsNewRequestDetailsStep />
        ) : (
          <AnalyticDetailsRequestPreviewStep isPrivate />
        )}
      </AnalyticDetailsLayout>
      {postPreviewError && (
        <GlDialog
          open={displayPreviewErrorDialog}
          onClose={() => {
            setDisplayPreviewErrorDialog(false)
          }}
          backgroundImage={BackGroundPreviewTypeError}
        >
          <div
            className="flex flex-col gap-2 justify-end"
            style={{
              width: 580,
              height: 312,
            }}
          >
            <GlDialogTitle
              label={
                (postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.SUBJECT_TOO_SMALL &&
                  t('NotEnoughDataOnSubject')) ||
                (postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.COMPARISON_TOO_SMALL &&
                  t('NotEnoughDataOnComparison')) ||
                ''
              }
            />
            <GlDialogBody color="#252525">
              <div>
                {postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.SUBJECT_TOO_SMALL &&
                  t('SubjectTooSmall')}
                {postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.COMPARISON_TOO_SMALL &&
                  t('ComparisonTooSmall')}
              </div>
              <div>{t('MayChangeSearch')}</div>
            </GlDialogBody>
            <div className="flex flex-row justify-between items-center gap-4">
              <GLDialogButtonStyle
                type="button"
                className="relative bg-[#252525] flex-1 border-[#252525] mt-6 hover:opacity-60 active:opacity-100"
                primary
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault()
                  setDisplayPreviewErrorDialog(false)
                }}
              >
                <span className="text-[#ffffff] font-medium">
                  {t('commons.buttons.close')}
                </span>
              </GLDialogButtonStyle>
            </div>
          </div>
        </GlDialog>
      )}
      <AnalyticsDialogExit
        open={saveBeforeExitDialog}
        onValidate={onValidateExitDialog}
        onClose={onCloseExitDialog}
      />
    </>
  )
}
export default PrivateNewReadershipProfileView
